<template>
  <div
    class="upload-content flex-grow-1 pt-4"
  >
    <div
      ref="parentDiv"
      class="tokenized-transcription-content text-unselectable"
      @click.right="handleRightClick"
      @mousedown="handleMouseDown"
      @mouseup="maybeShowRightClickMenu"
    >
      <div
        v-if="inTranslationMode && (!translationContent || retranslate)"
        class="translation-wrapper"
      >
        <div class="translation-box">
          <template v-if="!translationContent">
            <div>No translation exists for this transcript.</div>
            <div>You may create a translation here.</div>
          </template>
          <template v-else>
            <div>A translation already exists for this transcript.</div>
            <div>You may re-translate if desired.</div>
          </template>
          <div
            class="p-3 d-flex"
            style="justify-content: center"
          >
            <base-button
              v-if="retranslate"
              simple
              @click="() => retranslate = false"
            >
              Cancel
            </base-button>
            <base-button
              size="sm"
              type="primary"
              simple
              @click="doTranslation"
            >
              <plus-icon
                class="mr-2"
                size="1.5x"
              />
              Translate
            </base-button>
          </div>
        </div>
      </div>
      <template v-else>
        <template v-for="(c, index) in speakers">
          <template v-if="inTranslationMode">
            <div
              :key="`${c.speakerPos}-${index}`"
              class="d-flex"
            >
              <!-- LHS speakers -->
              <div
                class="col-6 d-flex align-items-baseline spk-container on-side lhs"
                style="padding-left: 0"
              >
                <el-tooltip
                  v-if="showUploadsPopover && speakerChangeRequests[c.speakerPos]"
                  :key="`${c.speakerPos}-${metadata.requestedChanges.length}`"
                  transition=""
                >
                  <transcription-speaker
                    :ref="`spk-${c.speakerPos}`"
                    :data-spk="c.speakerPos"
                    :detail="c"
                    :disabled="disabled"
                    :in-translation-mode="inTranslationMode"
                    @select="selectSpeaker"
                    @remove="removeSpeaker"
                  />
                  <template slot="content">
                    <change-requests

                      :scrs="speakerChangeRequests[c.speakerPos]"
                      :actor-map="actorMap"
                      :user-map="userMap"
                      side="lhs"
                      @approve="approve"
                      @reject="reject"
                      @undo="undoChangeRequest"
                    />
                  </template>
                </el-tooltip>
                <transcription-speaker
                  v-else
                  :ref="`spk-${c.speakerPos}`"
                  :data-spk="c.speakerPos"
                  :detail="c"
                  :disabled="disabled"
                  :in-translation-mode="inTranslationMode"
                  @select="selectSpeaker"
                  @remove="removeSpeaker"
                />
                <div
                  v-if="c.content.length > 0"
                  class="ml-auto speaker-time-series"
                  contenteditable="false"
                >
                  {{ formatTime(c.content[0].startOffset) }}
                </div>
              </div>
              <!-- RHS speakers -->
              <div
                class="col-6 d-flex align-items-baseline spk-container on-side rhs"
                style="padding-right: 0"
              >
                <el-tooltip
                  v-if="showUploadsPopover && speakerChangeRequests[c.speakerPos]"
                  :key="`${c.speakerPos}-${metadata.requestedChanges.length}`"
                  transition=""
                >
                  <transcription-speaker
                    :ref="`spk-${c.speakerPos}`"
                    :data-spk="c.speakerPos"
                    :detail="c"
                    :disabled="disabled"
                    :in-translation-mode="inTranslationMode"
                    @select="selectSpeaker"
                    @remove="removeSpeaker"
                  />
                  <template slot="content">
                    <change-requests

                      :scrs="speakerChangeRequests[c.speakerPos]"
                      :actor-map="actorMap"
                      :user-map="userMap"
                      side="rhs"
                      @approve="approve"
                      @reject="reject"
                      @undo="undoChangeRequest"
                    />
                  </template>
                </el-tooltip>
                <transcription-speaker
                  v-else
                  :ref="`spk-${c.speakerPos}`"
                  :data-spk="c.speakerPos"
                  :detail="c"
                  :disabled="disabled"
                  :in-translation-mode="inTranslationMode"
                  @select="selectSpeaker"
                  @remove="removeSpeaker"
                />
                <div
                  v-if="c.content.length > 0"
                  class="ml-auto speaker-time-series"
                  contenteditable="false"
                >
                  {{ formatTime(c.content[0].startOffset) }}
                </div>
              </div>
            </div>
            <div
              :key="'c-' + c.speakerPos + '-' + index + '-' + spanContainerKey"
              class="d-flex"
            >
              <!-- LHS span containers -->
              <div
                class="col-6 mt-2 mb-4 span-container on-side lhs"
                style="padding-left: 0"
                :class="{
                  'text-selectable': editSide === 'lhs',
                  'highlight-edit-section': isLHSEditMode,
                  'has-span': (
                    c.content && c.content.length > 0 &&
                    c.content[0].startOffset <= currentPlaytimeWithOffset &&
                    c.content[c.content.length -1].endOffset >= currentPlaytimeWithOffset &&
                    (!hasClip || (hasClip && clip.startOffset / 1000 != currentPlaytimeWithOffset))
                  ),
                }"
                :data-i="c.speakerPos"
                :contenteditable="isLHSEditMode"
                @keydown.enter.prevent=""
                @dragstart.prevent=""
                @drag.prevent=""
                @dragend.prevent=""
                @dragover.prevent=""
                @drop.prevent=""
                @dragenter.prevent=""
                @dragleave.prevent=""
              >
                <template v-for="(span, spanIx) in c.content">
                  <template
                    v-if="showUploadsPopover &&
                      editingSpeakerAt != c.speakerPos &&
                      (speakerChangeRequests[span.start] || contentChangeRequests[span.start])
                    "
                  >
                    <el-tooltip

                      :key="`${span.start}-${span.end}-${span.content}-${metadata.requestedChanges.length}-${spanIx}`"
                      transition=""
                    >
                      <change-requests

                        slot="content"
                        :scrs="speakerChangeRequests[span.start]"
                        :crs="contentChangeRequests[span.start]"
                        :actor-map="actorMap"
                        :user-map="userMap"
                        side="lhs"
                        @approve="approve"
                        @reject="reject"
                        @undo="undoChangeRequest"
                      />
                      <transcription-span
                        :ref="`trSpan-lhs${span.start}-${span.end}`"
                        :span="span"
                        :speaker-pos="c.speakerPos"
                        :incidents="incidents"
                        :unclear-threshold="unclearThreshold"
                        :current-playtime="currentPlaytimeWithOffset"
                        :keyword-indices="keywordIndices"
                        :keyword-current-index="keywordCurrentIndex"
                        :signal-matches="signalMatches"
                        :audio-ready="activeSpanClicks"
                        :clip-range="clipRange"
                        :is-editing="isLHSEditMode"
                        :signal-value="signalValue"
                        :unclear-value="unclearValue"
                        has-change-request
                        @seek="onSeek"
                        @index="(ind) => $emit('index', ind)"
                        @incident="editIncident"
                        @nextSpeaker="goToNextSpeaker"
                        @prevSpeaker="goToPrevSpeaker"
                        @isPlaying="(el) => $emit('spanPlaying', el)"
                      />
                    </el-tooltip>
                  </template>

                  <!-- eslint-disable-next-line vue/valid-v-for -->
                  <transcription-span
                    v-else
                    :ref="`trSpan-lhs${span.start}-${span.end}`"
                    :span="span"
                    :speaker-pos="c.speakerPos"
                    :incidents="incidents"
                    :unclear-threshold="unclearThreshold"
                    :current-playtime="currentPlaytimeWithOffset"
                    :keyword-indices="keywordIndices"
                    :keyword-current-index="keywordCurrentIndex"
                    :signal-matches="signalMatches"
                    :audio-ready="activeSpanClicks"
                    :clip-range="clipRange"
                    :is-editing="isLHSEditMode"
                    :signal-value="signalValue"
                    :unclear-value="unclearValue"
                    @seek="onSeek"
                    @index="(ind) => $emit('index', ind)"
                    @incident="editIncident"
                    @nextSpeaker="goToNextSpeaker"
                    @prevSpeaker="goToPrevSpeaker"
                    @isPlaying="(el) => $emit('spanPlaying', el)"
                  />
                </template>
              </div>
              <!-- RHS span containers -->
              <div
                v-if="c.translationContent && c.translationContent.length > 0"
                class="col-6 mt-2 mb-4 span-container on-side rhs"
                :class="{
                  'text-selectable': editSide === 'rhs',
                  'highlight-edit-section': isRHSEditMode,
                }"
                :data-i="c.speakerPos"
                :contenteditable="isRHSEditMode"
                @keydown.enter.prevent=""
                @dragstart.prevent=""
                @drag.prevent=""
                @dragend.prevent=""
                @dragover.prevent=""
                @drop.prevent=""
                @dragenter.prevent=""
                @dragleave.prevent=""
              >
                <template v-for="(span, spanIx) in c.translationContent">
                  <template
                    v-if="showUploadsPopover &&
                      editingSpeakerAt != c.speakerPos &&
                      (translationChangeRequests[span.start])
                    "
                  >
                    <el-tooltip

                      :key="`${span.start}-${span.end}-${span.content}-${metadata.requestedChanges.length}-${spanIx}`"
                      transition=""
                    >
                      <change-requests

                        slot="content"
                        :crs="translationChangeRequests[span.start]"
                        :actor-map="actorMap"
                        :user-map="userMap"
                        side="rhs"
                        @approve="approve"
                        @reject="reject"
                        @undo="undoChangeRequest"
                      />
                      <transcription-span
                        :ref="`trSpan-rhs${span.start}-${span.end}`"
                        :span="span"
                        :speaker-pos="c.speakerPos"
                        :incidents="incidents"
                        :unclear-threshold="unclearThreshold"
                        :current-playtime="currentPlaytimeWithOffset"
                        :keyword-indices="keywordIndices"
                        :keyword-current-index="keywordCurrentIndex"
                        :signal-matches="signalMatches"
                        :audio-ready="activeSpanClicks"
                        :clip-range="clipRange"
                        :is-editing="isRHSEditMode"
                        :signal-value="signalValue"
                        :unclear-value="unclearValue"
                        side="rhs"
                        has-change-request
                        @seek="onSeek"
                        @index="(ind) => $emit('index', ind)"
                        @incident="editIncident"
                        @nextSpeaker="goToNextSpeaker"
                        @prevSpeaker="goToPrevSpeaker"
                        @isPlaying="(el) => $emit('spanPlaying', el)"
                      />
                    </el-tooltip>
                  </template>

                  <!-- eslint-disable-next-line vue/valid-v-for -->
                  <transcription-span
                    v-else
                    :ref="`trSpan-rhs${span.start}-${span.end}`"
                    :span="span"
                    :speaker-pos="c.speakerPos"
                    :incidents="incidents"
                    :unclear-threshold="unclearThreshold"
                    :current-playtime="currentPlaytimeWithOffset"
                    :keyword-indices="keywordIndices"
                    :keyword-current-index="keywordCurrentIndex"
                    :signal-matches="signalMatches"
                    :audio-ready="activeSpanClicks"
                    :clip-range="clipRange"
                    :is-editing="isRHSEditMode"
                    :signal-value="signalValue"
                    :unclear-value="unclearValue"
                    side="rhs"
                    @seek="onSeek"
                    @index="(ind) => $emit('index', ind)"
                    @incident="editIncident"
                    @nextSpeaker="goToNextSpeaker"
                    @prevSpeaker="goToPrevSpeaker"
                    @isPlaying="(el) => $emit('spanPlaying', el)"
                  />
                </template>
              </div>
            </div>
          </template>
          <template v-else>
            <div
              :key="`${c.speakerPos}-${index}`"
              class="d-flex align-items-baseline spk-container on-side lhs"
            >
              <el-tooltip
                v-if="showUploadsPopover && speakerChangeRequests[c.speakerPos]"
                :key="`${c.speakerPos}-${metadata.requestedChanges.length}`"
                transition=""
              >
                <transcription-speaker
                  :ref="`spk-${c.speakerPos}`"
                  :data-spk="c.speakerPos"
                  :detail="c"
                  :disabled="disabled"
                  :in-translation-mode="inTranslationMode"
                  @select="selectSpeaker"
                  @remove="removeSpeaker"
                />
                <template slot="content">
                  <change-requests

                    :scrs="speakerChangeRequests[c.speakerPos]"
                    :actor-map="actorMap"
                    :user-map="userMap"
                    side="lhs"
                    @approve="approve"
                    @reject="reject"
                    @undo="undoChangeRequest"
                  />
                </template>
              </el-tooltip>
              <transcription-speaker
                v-else
                :ref="`spk-${c.speakerPos}`"
                :data-spk="c.speakerPos"
                :detail="c"
                :disabled="disabled"
                :in-translation-mode="inTranslationMode"
                @select="selectSpeaker"
                @remove="removeSpeaker"
              />
              <div
                v-if="c.content.length > 0"
                class="ml-auto speaker-time-series"
                contenteditable="false"
              >
                {{ formatTime(c.content[0].startOffset) }}
              </div>
            </div>
            <div
              :key="'c-' + c.speakerPos + '-' + index + '-' + spanContainerKey"
              class="mt-2 mb-4 span-container on-side lhs"
              :class="{
                'text-selectable': editSide === 'lhs',
                'highlight-edit-section': isLHSEditMode,
                'has-span': (
                  c.content && c.content.length > 0 &&
                  c.content[0].startOffset <= currentPlaytimeWithOffset &&
                  c.content[c.content.length -1].endOffset >= currentPlaytimeWithOffset &&
                  (!hasClip || (hasClip && clip.startOffset / 1000 != currentPlaytimeWithOffset))
                ),
              }"
              :data-i="c.speakerPos"
              :contenteditable="isLHSEditMode"
              @keydown.enter.prevent=""
              @dragstart.prevent=""
              @drag.prevent=""
              @dragend.prevent=""
              @dragover.prevent=""
              @drop.prevent=""
              @dragenter.prevent=""
              @dragleave.prevent=""
            >
              <template v-for="(span, spanIx) in c.content">
                <template
                  v-if="showUploadsPopover &&
                    editingSpeakerAt != c.speakerPos &&
                    (speakerChangeRequests[span.start] || contentChangeRequests[span.start])
                  "
                >
                  <el-tooltip

                    :key="`${span.start}-${span.end}-${span.content}-${metadata.requestedChanges.length}-${spanIx}`"
                    transition=""
                  >
                    <change-requests

                      slot="content"
                      :scrs="speakerChangeRequests[span.start]"
                      :crs="contentChangeRequests[span.start]"
                      :actor-map="actorMap"
                      :user-map="userMap"
                      side="lhs"
                      @approve="approve"
                      @reject="reject"
                      @undo="undoChangeRequest"
                    />
                    <transcription-span
                      :ref="`trSpan-lhs${span.start}-${span.end}`"
                      :span="span"
                      :speaker-pos="c.speakerPos"
                      :incidents="incidents"
                      :unclear-threshold="unclearThreshold"
                      :current-playtime="currentPlaytimeWithOffset"
                      :keyword-indices="keywordIndices"
                      :keyword-current-index="keywordCurrentIndex"
                      :signal-matches="signalMatches"
                      :audio-ready="activeSpanClicks"
                      :clip-range="clipRange"
                      :is-editing="isLHSEditMode"
                      :signal-value="signalValue"
                      :unclear-value="unclearValue"
                      has-change-request
                      @seek="onSeek"
                      @index="(ind) => $emit('index', ind)"
                      @incident="editIncident"
                      @nextSpeaker="goToNextSpeaker"
                      @prevSpeaker="goToPrevSpeaker"
                      @isPlaying="(el) => $emit('spanPlaying', el)"
                    />
                  </el-tooltip>
                </template>

                <!-- eslint-disable-next-line vue/valid-v-for -->
                <transcription-span
                  v-else
                  :ref="`trSpan-lhs${span.start}-${span.end}`"
                  :span="span"
                  :speaker-pos="c.speakerPos"
                  :incidents="incidents"
                  :unclear-threshold="unclearThreshold"
                  :current-playtime="currentPlaytimeWithOffset"
                  :keyword-indices="keywordIndices"
                  :keyword-current-index="keywordCurrentIndex"
                  :signal-matches="signalMatches"
                  :audio-ready="activeSpanClicks"
                  :clip-range="clipRange"
                  :is-editing="isLHSEditMode"
                  :signal-value="signalValue"
                  :unclear-value="unclearValue"
                  @seek="onSeek"
                  @index="(ind) => $emit('index', ind)"
                  @incident="editIncident"
                  @nextSpeaker="goToNextSpeaker"
                  @prevSpeaker="goToPrevSpeaker"
                  @isPlaying="(el) => $emit('spanPlaying', el)"
                />
              </template>
            </div>
          </template>
        </template>
      </template>
      <ul
        v-if="rightClickMenuVisible"
        id="dmenu"
        ref="dmenu"
        class="el-dropdown-menu el-popper"
        :style="{ top: menuY + 'px', left: menuX + 'px', display: 'block' }"
      >
        <li
          v-if="highlightedRange !== null && !isLHSEditMode && !isRHSEditMode && !inTranslationMode &&
            selectionWithinSpeaker && !selectionStartsFirstWord"
          tabindex="0"
          class="el-dropdown-menu__item"
          @click="quickAssignSpeaker"
        >
          Separate Speaker
        </li>
        <li
          v-if="!isLHSEditMode && !isRHSEditMode && !inTranslationMode &&
            highlightedRange === null &&
            rightClickTarget &&
            isSpan(rightClickTarget) &&
            rightClickTarget.className.indexOf('tspan') !== -1"
          tabindex="1"
          class="el-dropdown-menu__item"
          @click="insertSpeakerHere"
        >
          Insert Speaker Here
        </li>
        <li
          v-if="false && !isLHSEditMode && !isRHSEditMode && !inTranslationMode &&
            rightClickTarget.className.indexOf('tspan') === -1"
          tabindex="2"
          class="el-dropdown-menu__item divided"
          @click="assignToSpeaker"
        >
          Change Speaker
        </li>
        <li
          v-if="!isLHSEditMode && !isRHSEditMode"
          tabindex="3"
          class="el-dropdown-menu__item"
          @click="menuItemEdit"
        >
          Edit
        </li>
        <li
          v-if="!isLHSEditMode && !isRHSEditMode && editSide === 'rhs'"
          tabindex="3"
          class="el-dropdown-menu__item"
          @click="doRetranslate"
        >
          Re-Translate
        </li>
        <li
          v-if="!isLHSEditMode && !isRHSEditMode"
          tabindex="4"
          class="el-dropdown-menu__item"
          @click="showRevertConfirmation()"
        >
          Revert All Changes...
        </li>

        <li
          v-if="!isLHSEditMode && !isRHSEditMode &&
            focusedNodes.length === 1 && focusedNodes[0].className.indexOf('unclear') > -1"
          tabindex="5"
          class="el-dropdown-menu__item"
          @click="markClear"
        >
          Mark Word Correct
        </li>

        <li
          v-if="!isLHSEditMode && !isRHSEditMode &&
            focusedNodes.length === 1 && focusedNodes[0].className.indexOf('redacted') > -1"
          tabindex="6"
          class="el-dropdown-menu__item"
          @click="unRedactContent"
        >
          Remove Redaction
        </li>

        <li
          v-if="!isLHSEditMode && !isRHSEditMode && selectionWithinSpeaker && (
            highlightedRange !== null ||
            (rightClickTarget &&
              isSpan(rightClickTarget) &&
              rightClickTarget.className.indexOf('tspan') !== -1))"
          tabindex="7"
          class="el-dropdown-menu__item"
          @click="redactContent"
        >
          Redact Content
        </li>
        <li
          v-if="!isLHSEditMode && !isRHSEditMode && editSide === 'lhs' && focusedNodes.length > 0 &&
            focusedNodes.filter((f) => f.className.indexOf('incident') === -1).length === 0"
          tabindex="8"
          class="el-dropdown-menu__item"
          @click="viewIncident"
        >
          View Clip
        </li>

        <li
          v-if="!isLHSEditMode && !isRHSEditMode && editSide === 'lhs' && highlightedRange !== null && !hasClip"
          tabindex="9"
          class="el-dropdown-menu__item"
          @click="createIncident"
        >
          {{ displayIncidents ? 'Save Selection' : 'Create Clip' }}
        </li>
        <li
          v-if="selection !== null"
          tabindex="10"
          class="el-dropdown-menu__item"
          @click="() => copyHighlightedText(true)"
        >
          Copy
        </li>
        <li
          v-if="highlightedRange !== null && !isLHSEditMode && !isRHSEditMode && selectionWithinSpeaker"
          tabindex="11"
          class="el-dropdown-menu__item"
          @click="addCaseKeyword"
        >
          Add Case Keyword
        </li>
      </ul>
      <incident-form-modal
        ref="incidentForm"
        @created="createdIncident"
        @updated="updatedIncident"
        @close="closeMenu"
      />
      <add-people-modal
        ref="assignToSpeakerForm"
        :case-id="caseId"
        :upload-id="uploadId"
        associate
        @associated="assignContentToSpeaker"
        @created="assignContentToSpeaker"
        @close="closeAssignSpeakerForm"
      />
      <clip-form-modal
        ref="clipForm"
        :upload-id="uploadId"
        @clip="(c) => $emit('updatedClip', c)"
        @close="handleClipFormClose"
      />
      <confirmation-dialog
        :show="showRevertConfirm"
        :text="revertMessage"
        @okClicked="revertChanges()"
        @cancelClicked="showRevertConfirm = false"
      />
      <add-keyword-modal
        ref="addKeyword"
      />
    </div>
  </div>
</template>

<script>
import {postChangeRequests, postTranslationChangeRequests, postChangeRequestApproval, postMarkClear,
  postSpeakerChangeRequest, postRemoveRedaction, postRedaction,
  postTranslationRedaction, postRemoveTranslationRedaction, postTranslation,
  addActor, attributeActor, postRevertTranscription, postRevertTranslation} from "../../../../api";
import {mapActions, mapGetters, mapMutations} from "vuex";
import TranscriptionSpeaker from "./TranscriptionSpeaker.vue";
import ChangeRequests from "./ChangeRequests.vue";
import IncidentFormModal from "../IncidentFormModal.vue";
import AddPeopleModal from "../../../../components/DashboardV2/Case/AddPeopleModal.vue";
import AddKeywordModal from "../../../../components/DashboardV2/Case/AddKeywordModal.vue";
import ClipFormModal from "../ClipFormModal.vue";
import {isDefined} from "../../../../api/helpers";
import TranscriptionSpan from "./TranscriptionSpan.vue";
import {IncidentType} from "../../../../util/enums";
import {ethosRouteNames} from "../../../../routes/routeNames";
import {convertTranscriptDomToText, transcriptionMetadataToSpans,
  translationMetadataToSpans, transcriptionMetadataToSpeakers} from "../../../../util/exportUtil";
import ConfirmationDialog from "@/components/ConfirmationDialog.vue";
import {PlusIcon} from "vue-feather-icons";

export default {
  components: {
    ChangeRequests,
    TranscriptionSpeaker,
    ClipFormModal,
    IncidentFormModal,
    AddPeopleModal,
    AddKeywordModal,
    TranscriptionSpan,
    ConfirmationDialog,
    PlusIcon,
  },
  name: "TokenizedTranscriptionContent",
  props: {
    content: {
      type: String,
      default: null,
    },
    metadata: {
      type: Object,
      default: null,
    },
    uploadId: {
      type: Number,
      default: null,
    },
    transcriptionId: {
      type: Number,
      default: 0,
    },
    transcriptionName: {
      type: String,
      default: "Transcript",
    },
    incidents: {
      type: Array,
      default() {
        return [];
      },
    },
    currentPlaytime: {
      type: Number,
      default: 0,
    },
    keywordIndices: {
      type: Array,
      default() {
        return [];
      },
    },
    keywordCurrentIndex: {
      type: Number,
      default: -1,
    },
    signalMatches: {
      type: Array,
      default() {
        return [];
      },
    },
    clip: {
      type: Object,
      default: null,
    },
    signalValue: {
      type: Boolean,
      default: true,
    },
    unclearValue: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    translationContent: {
      type: String,
      default: null,
    },
    filterTerm: {
      type: String,
      default: null,
    },
  },
  watch: {
    highlightedRange: function(n, o) {
      if (n == null && n != o) {
        const isFormOpen = this.$refs.assignToSpeakerForm.isDisplayed() ||
          this.$refs.clipForm.isDisplayed ||
          this.$refs.incidentForm.isDisplayed;
        if (isFormOpen) {
          this.highlightedRange = o;
        } else {
          this.highlightOnContentRange(o.nodes.start, o.nodes.end, "selected", true);
        }
      }
    },
    "$route.name": {
      handler(to, from) {
        this.inTranslationMode = (to === ethosRouteNames.Translation);
      },
    },
  },
  computed: {
    ...mapGetters("auth", [
      "userEmail",
    ]),
    ...mapGetters("data", [
      "getUploads",
      "getActorMap",
      "getUserMap",
      "displayIncidents",
      "showUploadsPopover",
      "highlightSnippet",
      "highlightSnippetSide",
    ]),
    caseId() {
      const id = this.$route.params.caseId;
      return isDefined(id) ? parseInt(id, 10) : null;
    },
    hasClip() {
      return isDefined(this.clip);
    },
    currentPlaytimeWithOffset() {
      const clipOffset = this.hasClip ? this.clip.startOffset / 1000 : 0;
      return this.currentPlaytime + clipOffset;
    },
    contentChangeRequests() {
      if (!this.metadata || !this.metadata.requestedChanges) return [];
      const t = this.metadata.requestedChanges
        .filter((rc) => rc.type == "E")
        .reduce((all, rc) => {
          if (!all[rc.s]) all[rc.s] = [];
          all[rc.s].unshift(rc); // newest first
          return all;
        }, {});
      return t;
    },
    translationChangeRequests() {
      if (!this.metadata || !this.metadata.translationChanges) return [];
      const t = this.metadata.translationChanges
        .filter((rc) => rc.type == "E")
        .reduce((all, rc) => {
          if (!all[rc.s]) all[rc.s] = [];
          all[rc.s].unshift(rc); // newest first
          return all;
        }, {});
      return t;
    },
    speakerChangeRequests() {
      if (!this.metadata || !this.metadata.requestedChanges) return [];
      const combined = {};
      this.metadata.requestedChanges
        .filter((rc) => rc.type == "S")
        .reduce((all, rc) => {
          if (!all[rc.s]) all[rc.s] = [];
          all[rc.s].unshift(rc); // newest first
          return all;
        }, combined);
      return combined;
    },
    speakers() {
      return this.getSpeakers(this.metadata, this.content, this.spans, this.translationContent, this.translationSpans);
    },
    spans() {
      this.reloadCount;
      return this.getSpans(this.metadata, this.content);
    },
    translationSpans() {
      this.reloadCount;
      return this.getTranslationSpans(this.metadata, this.translationContent);
    },
    userMap() {
      return this.getUserMap;
    },
    actorMap() {
      return this.getActorMap;
    },
  },
  data() {
    return {
      activeSpanClicks: false,
      currentHighlight: {
        line: -1,
        index: -1,
      },
      clipRange: null,
      // tslookup: [],
      editingSpeakerAt: -1,
      unclearThreshold: 75,
      transcriptIndex: null,
      focusedSection: -1,
      focusedNodes: [],
      highlightedRange: null,
      cases: [],
      isLHSEditMode: false,
      isRHSEditMode: false,
      editModeTransitionEnded: {},
      editObserver: null,
      isSpanRightClicked: false,
      rightClickTarget: null,
      removedSpans: [],
      loading: true,
      assignSpeakerEventData: null,
      rightClickMenuVisible: false,
      menuX: 0,
      menuY: 0,
      selection: null,
      blockSelectionUpdate: false,
      selectionWithinSpeaker: false,
      selectionStartsFirstWord: false,
      reloadCount: 0,
      showRevertConfirm: false,
      revertMessage: "",
      spanContainerKey: 0,
      editSide: "none",
      inTranslationMode: false,
      retranslate: false,
    };
  },
  mounted() {
    this.inTranslationMode = this.$route.name === "Translation";
    if (
      Object.keys(this.getUserMap).length +
      Object.keys(this.getActorMap).length === 0 ) {
      this.doLoadPeople(true);
    } else {
      this.loading = false;
    }
    document.addEventListener("copy", this.copyEventListener);
    document.addEventListener("selectionchange", this.selectionChangeListener);
    window.setTimeout(() => {
      try {
        if (this.highlightSnippet) {
          const side = this.highlightSnippetSide ?? "lhs";
          if (side === "rhs") {
            const index = this.translationContent.indexOf(this.highlightSnippet.trim());
            if (index > -1) {
              const next = this.translationSpans.findIndex((s) => s.start > index);
              if (next < 1) next = 0;
              const start = this.translationSpans[next - 1];
              this.scrollToSnippet(start, 0, side);
            }
          } else {
            const index = this.content.indexOf(this.highlightSnippet.trim());
            if (index > -1) {
              const next = this.spans.findIndex((s) => s.start > index);
              if (next < 1) next = 0;
              const start = this.spans[next - 1];
              this.scrollToSnippet(start, 0, side);
            }
          }
          this.putHighlightSnippet(null);
          this.putHighlightSnippetSide(null);
        }
      } catch (ex) {
        // ignore
      }
    }, 50);
  },
  beforeDestroy() {
    this.stopObserving();
    document.removeEventListener("copy", this.copyEventListener);
    document.removeEventListener("selectionchange", this.selectionChangeListener);
    document.removeEventListener("click", this.clickListener);
  },
  methods: {
    ...mapActions("data", ["loadPeople", "loadActors", "loadUsers"]),
    ...mapMutations("data", ["putHighlightSnippet", "putHighlightSnippetSide"]),
    scrollToSnippet(start, tryNum, side) {
      const el = document.querySelector(`span[data-key="${side}${start.start}-${start.end}"]`);
      if (el) {
        el.scrollIntoView();
      }
      else {
        if (tryNum < 30) { // don't keep trying forever
          window.setTimeout(() => this.scrollToSnippet(start, tryNum + 1, side), 50);
        }
      }
    },
    beforeUnloadHandler(event) {
      event.preventDefault();
      // Included for legacy support, e.g. Chrome/Edge < 119
      event.returnValue = true;
    },
    startObserving() {
      if (this.observer) {
        return false;
      }
      const config = {characterData: true, childList: true, subtree: true};

      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "characterData") {
            // This should only be firing when mutation.target is a text node.
            // We want to fire the onInput event on spans that have a data-key attribute,
            // but not every text node is wrapped in a span with a data-key. When it's
            // got a data-key, we fire onInput. When it's not, we ignore the mutation.
            // Text nodes that are not within spans with data-keys will be detected
            // and handled when the user saves the edits.
            const dataKey = mutation.target.parentElement.attributes["data-key"];
            if (dataKey) {
              this.$refs[`trSpan-${dataKey.value}`][0].onInput(mutation.target.data);
            }
          }
          if (mutation.type === "childList") {
            // deleted words will be tracked here so that when save is called
            // we can apply those deletions to the content
            mutation.removedNodes.forEach((node) => {
              // We want to track span elements from the original render that are removed
              // in this.removedSpans. However, not every removed node we see here is a span
              // we're interested in. Capture the removed span if node is an element with a
              // data-o attribute.
              if (node instanceof Element && node.hasAttribute("data-o")) {
                if (node.dataset.o === " ") return;
                const clone = node.cloneNode(true);
                clone.style.visibility = "hidden";
                this.removedSpans.push(clone);
              }
            });
          }
        });
      });

      this.$refs.parentDiv.querySelectorAll(".span-container").forEach((node) => {
        // if (!this.observer.takeRecords().length) {
        this.observer.observe(node, config);
        // }
      });
      window.addEventListener("beforeunload", this.beforeUnloadHandler);
      return true;
    },
    stopObserving() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      window.removeEventListener("beforeunload", this.beforeUnloadHandler);
    },
    getSpans(metadata, content) {
      return transcriptionMetadataToSpans(metadata, content);
    },
    getTranslationSpans(metadata, translationContent) {
      return translationMetadataToSpans(metadata, translationContent);
    },
    getSpeakers(metadata, content, spans, translationContent, translationSpans) {
      if (this.loading) return [];
      return transcriptionMetadataToSpeakers(metadata, content, spans, translationContent, translationSpans);
    },
    triggerReload() {
      this.reloadCount++;
    },
    doLoadPeople(force) {
      if (this.loading) {
        if (force !== true) return;
      }
      this.loading = true;
      this.loadPeople().catch((ex) => {
        this.$notifyError("Loading personel info failed", ex);
      }).finally(() => {
        this.loading = false;
      });
    },
    // highlightWordBefore(currentTime) {
    //   for (let i = this.tslookup.length - 1; i >= 0; i--) {
    //     if (this.tslookup[i].start <= currentTime) {
    //       this.currentHighlight.line = this.tslookup[i].line;
    //       this.currentHighlight.index = this.tslookup[i].index;
    //       return;
    //     }
    //   }
    // },
    bindClicks() {
      this.activeSpanClicks = true;
    },
    handleClipFormClose() {
      this.clipRange = null;
      this.closeMenu();
    },
    handleRightClick(e) {
      if (this.disabled) return;
      e.preventDefault();
      this.blockSelectionUpdate = true;
      if (e && e.target) {
        this.rightClickTarget = e.target;
        this.showRightClickMenu(e);
      }
    },
    handleMouseDown(e) {
      if (this.disabled) return;
      if (e.button === 2) {
        // Anticipate a right-click, so set the flag to block updating the selection
        this.blockSelectionUpdate = true;
      } else {
        // For other mouse buttons, ensure normal behavior
        this.blockSelectionUpdate = false;
      }
      // User may be about to select some text.  Because of the multi-column translation display,
      // we need to control whether left or right column should be text-selectable. Use this
      // event to open up the span containers on left or right side for selection.
      const side = e.target.closest(".on-side");
      if (side) {
        if (side.classList.contains("lhs")) this.editSide = "lhs";
        else if (side.classList.contains("rhs")) this.editSide = "rhs";
        else this.editSide = "none";
      }
    },
    clickListener(e) {
      if (this.rightClickMenuVisible && this.$refs.dmenu && !this.$refs.dmenu.contains(e.target)) {
        this.closeMenu();
      }
    },
    selectionChangeListener(e) {
      if (!this.blockSelectionUpdate && !this.disabled) {
        setTimeout(() => {
          const s = window.getSelection();
          this.selection = s.rangeCount > 0 && !s.isCollapsed ? s.getRangeAt(0) : null;

          // if selecting a single word, we expand the selection to include dom element
          if (this.selection &&
          this.selection.nodeType === Node.TEXT_NODE &&
          this.selection.startContainer === this.selection.endContainer) {
            const parentElement = this.selection.startContainer.parentNode;

            // Create a new range that includes the parent element
            const newRange = document.createRange();
            this.selection.selectNode(parentElement);

            this.selection = newRange;
          }

          // Determine whether the selection is all within one speaker, or crossing multiple speakers
          this.selectionWithinSpeaker = false;
          if (this.selection) {
            const ancestor = this.selection.commonAncestorContainer;
            if (ancestor instanceof Element) {
              this.selectionWithinSpeaker = ancestor.className.includes("span-container");
            }
            else {
              // don't really know what this is, but often it's just part of a word.
              this.selectionWithinSpeaker = true;
            }
          }

          // Determine whether the selection starts on the first word within a speaker or not.
          this.selectionStartsFirstWord = false;
          if (this.selection) {
            const parentElement = this.selection.startContainer.parentElement;
            if (parentElement && parentElement.className.includes("tspan")) {
              if (parentElement instanceof HTMLElement) {
                const start = parentElement.dataset.s;
                const speaker = this.speakers.find((s) => s.speakerPos == start);
                if (speaker) {
                  this.selectionStartsFirstWord = true;
                }
              }
            }
          }
        }, 350);
      }
    },
    maybeShowRightClickMenu(e) {
      e.preventDefault();
      if (this.selection != null && this.highlightedRange === null) {
        this.showRightClickMenu(e);
      } else {
        // clear selection if RMC wasn't shown, like a double click scenario
        const s = window.getSelection();
        if (!s.isCollapsed) {
          s.removeAllRanges();
        }
      }
    },
    findWord(selectedNode, dir) {
      const validDirections = ["next", "previous"];
      dir = validDirections.includes(dir) ? dir : "next";
      let node = null;
      node = selectedNode.nodeType === Node.TEXT_NODE ? selectedNode.parentNode : selectedNode;
      let n = selectedNode;
      let isSpeaker = false;
      let isContent = false;
      while (n != null) {
        isSpeaker = n.parentNode.className?.indexOf("spk-container") > -1;
        isContent = n.parentNode.className?.indexOf("span-container") > -1;
        // break when parent node is span container, means we found content
        if (isContent) break;
        // break when parent node is speaker container, means we found a speaker
        if (isSpeaker) {
          // set to speaker container so that findWord can get the next/prev content
          n = n.parentNode;
          break;
        }
        n = n.parentNode;
      }
      node = n;
      if (!this.isSpan(node) || node.className.indexOf("span-container") > -1) {
        let n = dir === "next" ? node.nextSibling : node.previousSibling;
        while (n != null && n.className?.indexOf("span-container") === -1) {
          n = dir === "next" ? n.nextSibling : n.previousSibling;
        }
        return dir === "next" ? n?.firstChild : n?.lastChild;
      } else {
        return node;
      }
    },
    showRightClickMenu(e) {
      if (this.disabled) return;
      if (this.editingSpeakerAt >= 0) return;
      e.stopPropagation();
      e.preventDefault();
      try {
        this.focusedSection = parseInt(e.currentTarget.dataset["i"], 10);
      } catch (ex) {
        console.log("Unable to find section index");
      }
      this.focusedNodes = [e.target];
      const x = e.clientX;
      const y = e.clientY;
      try {
        if (e.target.contentEditable !== "true") {
          const range = this.selection;
          if (range && range.toString().trim().length > 0) {
            const anchorNode = this.findWord(range.startContainer, "next");
            const focusNode = this.findWord(range.endContainer, "previous");

            if (anchorNode && focusNode) {
              this.highlightOnContentRange(anchorNode, focusNode, "selected");
              const as = parseInt(anchorNode.getAttribute("data-s"), 10);
              const ae = parseInt(anchorNode.getAttribute("data-e"), 10);
              const fs = parseInt(focusNode.getAttribute("data-s"), 10);
              const fe = parseInt(focusNode.getAttribute("data-e"), 10);
              const start = Math.min(as, fs);
              const end = Math.max(ae, fe);
              const text = this.editSide === "rhs" 
                ? this.translationContent.slice(start, end)
                : this.content.slice(start, end);
              if (isDefined(start) && isDefined(end)) {
                this.highlightedRange = {
                  start,
                  end,
                  text: text,
                  type: IncidentType.transcript,
                  transcriptId: this.transcriptionId,
                  uploadId: this.uploadId,
                  nodes: {
                    start: as === start ? anchorNode : focusNode,
                    end: as === start ? focusNode : anchorNode,
                  },
                };
              }
            }
          }
        }
      } catch (ex) {
        console.error("Error showing right click menu", ex);
      }

      this.rightClickMenuVisible = true;
      // add an event listener after right click menu is shown,
      // if we add too early, the listener will catch the click
      // that is still being processed to show the RMC in this
      // context
      document.removeEventListener("click", this.clickListener);
      setTimeout(() => {
        document.addEventListener("click", this.clickListener);
      }, 100);

      this.$nextTick(() => {
        const menuRect = this.$refs.dmenu.getBoundingClientRect();
        const vpW = window.innerWidth;
        const vpH = window.innerHeight;
        this.menuX = x + menuRect.width > vpW ? x - menuRect.width : x;
        this.menuY = y + menuRect.height > vpH ? y - menuRect.height : y;
      });
    },
    onSeek(endTime) {
      this.clearHighlights();
      this.$emit("seek", endTime);
    },
    clearHighlights() {
      document.querySelectorAll("div.span-container span").forEach((el) => {
        try {
          const key = el.attributes["data-key"].value;
          const ref = this.$refs[`trSpan-${key}`][0];
          if (ref.isHighlighted) ref.isHighlighted = false;
        } catch (ex) {}
      });
    },
    isSpan(node) {
      return node.nodeName === "SPAN" || node.tagName === "SPAN";
    },
    // highlights content 1 way across next node siblings
    highlightOnContentRange(startNode, endNode, className, doRemove) {
      let where = "start";
      try {
        // this.clearHighlights();
        let sibling = null;
        sibling = startNode;
        while (sibling && (sibling.attributes["data-s"]?.value | 0) < (endNode.attributes["data-e"]?.value | 0)) {
          const key = sibling.attributes["data-key"].value;
          if (doRemove) {
            this.$refs[`trSpan-${key}`][0].isHighlighted = false;
          } else {
            if (sibling.className.indexOf("span-container") === -1) {
              this.$refs[`trSpan-${key}`][0].isHighlighted = true;
            }
          }

          if (sibling.nextSibling) {
            where = "sibling 1";
            sibling = sibling.nextSibling;
          } else {
            where = "sibling 2";
            sibling = sibling.parentNode.nextSibling;
            where = "sibling loop";
            while (sibling != null && sibling.className != null &&
              // check for span containers which should be text, but there may be cases of empty
              // span containers so we need to check for that as well
              (sibling.className.indexOf("span-container") === -1 || sibling.firstChild == null )) {
              sibling = sibling.nextSibling;
            }
            sibling = sibling?.firstChild;
          }
        }
      }

      catch {
        console.log("Highlight error", where);
      }
    },
    // replaceSection(speakerPos) {
    //   let first = true;
    //   document.querySelectorAll(`div.span-container[data-i="${speakerPos}"] span`).forEach((el) => {
    //     if (first) {
    //       first = false;
    //       el.focus();
    //     } else {
    //       el.style.visibility = "hidden";
    //     }
    //   });
    // },
    saveEdits() {
      if (this.disabled) return;
      this.closeMenu();
      this.doSaveEdits().then((t) => {
        this.$emit("updated", t);
        this.doCancelEdits();
        if (t) {
          this.$notifySuccess("Changes successful");
        }
      }).catch((ex) => {
        this.$notifyError("Unable to save changes", ex);
      });
    },
    doTranslation() {
      if (this.disabled) return;
      this.retranslate = false;
      postTranslation(this.transcriptionId).then((t) => {
        this.$emit("updated", t);
        if (t) {
          this.$notifySuccess("Translation successful");
        }
      }).catch((ex) => {
        this.$notifyError("Unable to complete translation", ex);
      });
    },
    getNodeTypeName(node) {
      const nodeTypeNames = {
        1: "ELEMENT_NODE",
        2: "ATTRIBUTE_NODE",
        3: "TEXT_NODE",
        4: "CDATA_SECTION_NODE",
        5: "ENTITY_REFERENCE_NODE",
        6: "ENTITY_NODE",
        7: "PROCESSING_INSTRUCTION_NODE",
        8: "COMMENT_NODE",
        9: "DOCUMENT_NODE",
        10: "DOCUMENT_TYPE_NODE",
        11: "DOCUMENT_FRAGMENT_NODE",
        12: "NOTATION_NODE"
      };
      return nodeTypeNames[node.nodeType] || "UNKNOWN_NODE_TYPE";
    },
    traverseDOM(element, lvl) {
      // Print node type and class name(s)
      if (element.nodeType === Node.ELEMENT_NODE) {
        console.log(
          `${lvl} Element: <${element.tagName.toLowerCase()}>, Classes: ${element.className}`
        );
      } else {
        console.log(`${lvl} ${this.getNodeTypeName(element)}, Text: "${element.textContent}"`);
      }
      // Recursively traverse children
      element.childNodes.forEach((child) => this.traverseDOM(child, lvl + 1));
    },
    logSpeakers(container) {
      const spanContainers = container.querySelectorAll(".span-container");
      this.traverseDOM(spanContainers[0], 0);
      // spanContainers.forEach( (cont, i) => {
      //   traverseDOM(cont);
      // });
    },
    processSpanContainer(element, changes, lvl) {
      let recurse = true;
      if (element.nodeType === Node.ELEMENT_NODE) {
        if (element.tagName.toLowerCase() === "span" && lvl === 1) {
          recurse = false;
          if (element.hasAttribute("data-s")) {
            const startIndex = element.getAttribute("data-s");
            const endIndex = element.getAttribute("data-e");
            const oldContent = element.getAttribute("data-o");
            const nTrimmed = element.textContent.trim();
            const oTrimmed = oldContent.trim();
            if (nTrimmed !== oTrimmed) {
              const originalIndex = oldContent.indexOf(oTrimmed);
              const newContent = oldContent.slice(0, originalIndex) + nTrimmed +
                oldContent.slice(originalIndex + oldContent.length);
              changes.push({startIndex, endIndex, newContent, oldContent});
            }
          } else {
            console.log("Level 1 element not span:", element.tagName, element.textContent, element.className);
          }
        }
      } else {
        console.log(`Not in L1 span: ${lvl} ${this.getNodeTypeName(element)}, Text: "${element.textContent}"`);
      }
      // Recursively traverse children
      if (recurse) {
        element.childNodes.forEach((child) => this.processSpanContainer(child, changes, lvl + 1));
      }
    },
    async doSaveEdits() {
      const container = document.querySelector(".tokenized-transcription-content");
      const hiddenGroups = this.findAdjacentHiddenSpans(this.removedSpans);
      this.removedSpans = [];
      const changes = [];
      if (hiddenGroups.length > 0) {
        changes.push(...this.prepareContentReplaceChanges(hiddenGroups));
      }
      for (const hg of hiddenGroups) {
        for (const sp of hg) {
          // remove the data-d attribute which indicates a change was made,
          // since this node was removed/hidden, it shouldn't be considered
          // as a "formatted" change.
          sp.removeAttribute("data-d");
        };
      }

      // Get the span containers just for the side being edited
      const spanContainers = Array.from(container.querySelectorAll(".span-container"))
        .filter((c) => c.classList.contains(this.editSide));

      spanContainers.forEach((cont) => {
        this.processSpanContainer(cont, changes, 0);
      });

      let newContent = null;

      if (changes.length > 0) {
        newContent = await this.contentChange(changes);
        this.spanContainerKey++;
      }

      for (const hg of hiddenGroups) {
        for (const sp of hg) {
          sp.style.visibility = "";
        };
      }
      return newContent;
    },
    findAdjacentHiddenSpans(spans) {
      const hiddenGroups = [];
      let currentGroup = [];
      let previousEndIndex = -1;
      let previousSpeaker = -1;

      // Following logic depends on spans being ascending by start index but user may have
      // deleted text "out of order". Need to sort.
      spans.sort((a, b) => parseInt(a.getAttribute("data-s"), 10) - parseInt(b.getAttribute("data-s"), 10));

      spans.forEach((span, i) => {
        const startIdx = parseInt(span.getAttribute("data-s"), 10);
        const endIdx = parseInt(span.getAttribute("data-e"), 10);
        const speaker = parseInt(span.getAttribute("data-spk"), 10);

        if (span.style.visibility === "hidden") {
          // If the start index is equal to the previous end index within the same speaker,
          // it's part of the same group
          if (startIdx === previousEndIndex && speaker == previousSpeaker) {
            currentGroup.push(span);
          } else {
            // Otherwise, push the current group to hiddenGroups and start a new one
            if (currentGroup.length > 0) {
              hiddenGroups.push(currentGroup);
            }
            currentGroup = [span];
          }
          previousEndIndex = endIdx;
          previousSpeaker = speaker;
        } else if (currentGroup.length > 0) {
          // If the span is not hidden and there is a current group, push the group to the result and start a new group
          hiddenGroups.push(currentGroup);
          currentGroup = [];
          previousEndIndex = -1; // Reset the previous end index since the current span is not hidden
          previousSpeaker = speaker;
        }
        // If we're on the last span and there's a current group, push the group to the result
        if (i === spans.length - 1 && currentGroup.length > 0) {
          hiddenGroups.push(currentGroup);
        }
      });

      return hiddenGroups; // Returns an array of arrays, where each sub-array is a group of adjacent hidden spans
    },
    cancelViaReload() {
      window.removeEventListener("beforeunload", this.beforeUnloadHandler);
      window.location.reload();
    },
    cancelEdits() {
      // called from outside of this component
      // const scrollState = this.captureScrollState();
      // console.log("scroll state", scrollState);
      this.doCancelEdits();
      // this.restoreScrollState(scrollState);
      // this.restoreScrollState2();
    },
    doCancelEdits() {
      // called from inside of this component
      this.closeMenu();
      this.isLHSEditMode = false;
      this.isRHSEditMode = false;
      this.stopObserving();
      this.editingSpeakerAt = -1;
      this.removedSpans = [];
      this.triggerReload();
      this.spanContainerKey++;
    },
    insertSpeakerHere() {
      const start = parseInt(this.rightClickTarget.dataset["s"], 10);
      this.insertSpeakerWithStart(start);
    },
    insertSpeaker() {
      if (this.focusedNodes.length > 0) {
        const start = parseInt(this.focusedNodes[0].dataset["s"], 10);
        this.insertSpeakerWithStart(start);
      }
    },
    insertSpeakerWithStart(start) {
      if (this.disabled) return;
      const me = this;
      postSpeakerChangeRequest(
        this.transcriptionId, start, null, null, false, true, null, null, null
      ).then((t) => {
        me.closeMenu();
        me.$emit("updated", t);
        me.$notifySuccess("Successfully added speaker");
      }).catch((ex) => {
        me.closeMenu();
        this.$notifyError(`Unable to add new Speaker: ${ex.message}`, ex);
      });
    },
    async quickAssignSpeaker(e) {
      if (this.highlightedRange == null) return;
      const unknownSpeakerName = "Unknown Speaker";
      const ix = Object.keys(this.actorMap).find((x) => this.actorMap[x].fullName === unknownSpeakerName);
      let quickAssignActor = this.actorMap[ix];
      let quickAssignActorName = unknownSpeakerName;
      try {
        const prevSpeaker = this.highlightedRange.nodes.start.parentNode.previousSibling;
        const speakerStart = prevSpeaker.childNodes[0].dataset.spk;
        const existingSpeaker = this.speakers.filter((s) => `${s.speakerPos}` === speakerStart).pop();
        if (existingSpeaker && `${ix}` === `${existingSpeaker.speakerActorId}`) {
          quickAssignActor = null;
          quickAssignActorName += " 2";
        }
      } catch (ex) {
        // Ignore error
      }

      if (!isDefined(quickAssignActor)) {
        quickAssignActor = await addActor(quickAssignActorName, null)
          .catch((ex) => {
            this.loading = false;
            this.$notifyError("Failed to create Unknown speaker actor.", ex);
          });

        await attributeActor(
          quickAssignActor.id,
          "-",
          this.caseId,
          this.uploadId,
          null
        ).catch((ex) => {
          this.$notifyError("Failed to assign Subject", ex);
          return;
        });

        await this.loadActors();
      }
      this.assignSpeakerEventData = Object.assign({}, this.highlightedRange);
      this.doAssignToSpeaker(quickAssignActor.id, null);
    },
    doAssignToSpeaker(actorId, userId) {
      if (!(isDefined(userId) || isDefined(actorId))) return;
      const eventData = this.assignSpeakerEventData;
      if (!isDefined(eventData)) return;
      postSpeakerChangeRequest(
        this.transcriptionId,
        eventData.start,
        Math.max(1, parseInt(eventData.end, 10) - 1),
        null,
        false,
        true,
        null,
        userId,
        actorId
      ).then((t) => {
        this.assignSpeakerEventData = null;
        this.$emit("updated", t);
        this.$notifySuccess("Speaker successfully changed");
      }).catch((ex) => {
        this.$notifyError(`Unable to assign new Speaker: ${ex.message}`, ex);
      }).finally(() => {
        this.closeMenu();
      });
    },
    assignContentToSpeaker(person) {
      if (this.disabled) return;
      const userId = isDefined(person) ? person.userId : null;
      const actorId = isDefined(person) ? person.actorId : null;
      this.doAssignToSpeaker(actorId, userId);
    },
    doRetranslate() {
      this.retranslate = true;
      this.closeMenu();
    },
    menuItemEdit(directionHint) {
      if (this.disabled || this.editSide === "none") return;
      this.editingSpeakerAt = this.focusedSection;
      const highlighting = this.highlightedRange && this.highlightedRange.nodes;
      this.closeMenu();
      // Get the span containers just for the side being edited
      const containers = Array.from(this.$refs.parentDiv.querySelectorAll(".span-container"))
        .filter((c) => c.classList.contains(this.editSide));
      containers.forEach((node) => {
        // transitionend event only firing of background-color with current styling
        const state = {
          "background-color": false,
        };
        this.editModeTransitionEnded[`i-${node.dataset.i}`] = state;
      });

      // listen for transitionend event on all span-containers and then start observing
      // this ensures we're not observing while the transition is happening
      containers.forEach((node) => {
        node.addEventListener("transitionend", (e) => {
          this.editModeTransitionEnded[`i-${node.dataset.i}`][e.propertyName] = true;
          if (this.checkAllPropertiesTrue(this.editModeTransitionEnded)) {
            if (this.startObserving()) {
              if (this.focusedNodes.length > 0) {
                const newNodes = this.focusedNodes.map((fn) => {
                  if (fn.id) return document.getElementById(fn.id);
                  return null;
                }).filter((nn) => nn);
                this.focusedNodes = newNodes;
                const start = highlighting ? document.getElementById(highlighting.start.id) : null;
                const end = highlighting ? document.getElementById(highlighting.start.id) : null;
                if (start && end) {
                  end.scrollIntoView({block: "start", inline: "nearest", behaviour: "instant"});
                  start.scrollIntoView({block: "start", inline: "nearest", behaviour: "instant"});
                  this.highlightSelectionNodes(document.getElementById(highlighting.start.id), document.getElementById(highlighting.end.id), "selection");
                } else if (this.focusedNodes.length === 1) {
                  this.focusedNodes[0].scrollIntoView({block: "start", inline: "nearest", behaviour: "instant"});
                  this.putCaretInNode(this.focusedNodes[0]);
                }
              }
            }
          }
        }, {once: true});
      });
      if (this.editSide === "rhs") {
        this.isRHSEditMode = true;
      } else {
        this.isLHSEditMode = true;
      }
    },
    async highlightSelectionNodes(start, end) {
      const p = start.parentNode;
      const pNodes = [...p.childNodes];
      const range = document.createRange();
      range.setStart(p, pNodes.indexOf(start));
      range.setEnd(p, pNodes.indexOf(end));
      const sel = window.getSelection();
      sel.empty();
      sel.addRange(range);
    },
    async putCaretInNode(node) {
      const range = document.createRange();
      range.setStart(node.childNodes[0], 0);
      const sel = window.getSelection();
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      node.focus();
      let anim = 5;
      while (anim > 0) {
        node.style.fontWeight = "1000";
        await new Promise((r) => setTimeout(r, 400));
        node.style.fontWeight = null;
        await new Promise((r) => setTimeout(r, 400));
        anim--;
      }
      node.style.transition = null;
    },
    checkAllPropertiesTrue(obj) {
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const nestedObj = obj[key];
          for (const prop in nestedObj) {
            if (nestedObj.hasOwnProperty(prop)) {
              if (nestedObj[prop] !== true) {
                return false; // Return false as soon as one property is not true
              }
            }
          }
        }
      }
      return true;
    },
    closeMenu() {
      document.removeEventListener("click", this.clickListener);
      this.menuTranscriptIndex = null;
      this.rightClickMenuVisible = false;
      this.menuX = 0;
      this.menuY = 0;
      this.highlightedRange = null;
      this.isSpanRightClicked = false;
      window.getSelection()?.empty();
    },
    prepareContentReplaceChanges(groups) {
      if (groups.length <= 0) throw new Error("No elements found");
      const changes = [];
      for (const els of groups) {
        const oldContent = els.reduce((all, el) => {
          all += el.dataset.o;
          return all;
        }, "");
        const startIndex = els[0].dataset.s;
        const endIndex = els[els.length - 1].dataset.e;
        const newContent = "";
        changes.push({startIndex, endIndex, newContent, oldContent});
      }
      return changes;
    },
    formatChange(el) {
      const oldContent = el.dataset.o;
      const startIndex = el.dataset.s;
      const endIndex = el.dataset.e;
      const oldContentTrimmed = oldContent.trim();
      const changedTo = el.innerText.trim();
      const originalIndex = oldContent.indexOf(oldContentTrimmed);
      const newContent = oldContent.slice(0, originalIndex) + changedTo + oldContent
        .slice(originalIndex + oldContent.length);
      return {startIndex, endIndex, newContent, oldContent};
    },
    async contentChange(formattedChanges) {
      if (this.isRHSEditMode) {
        return postTranslationChangeRequests(this.transcriptionId, formattedChanges);
      }
      if (this.isLHSEditMode) {
        return postChangeRequests(this.transcriptionId, formattedChanges);
      }
      return null; // Throw an error instead?
    },
    removeSpeaker(crDetail) {
      if (this.disabled || this.isLHSEditMode || this.isRHSEditMode) return;
      postSpeakerChangeRequest(
        this.transcriptionId, crDetail.speakerPos, null, null, true, false, null, null, null
      ).then((t) => {
        this.$emit("updated", t);
        this.$notifySuccess("Successfully removed peaker");
      }).catch((ex) => {
        this.$notifyError(`Unable to remove Speaker: ${ex.message}`, ex);
      });
    },
    selectSpeaker(crDetail) {
      if (this.disabled || this.isLHSEditMode || this.isRHSEditMode) return;
      const me = this;
      this.$emit("selectSpeaker", {
        uid: crDetail.speakerUserId,
        aid: crDetail.speakerActorId,
        startIndex: crDetail.speakerPos,
        callback: (opts, p, shouldMapMatching) => {
          if (!isDefined(p)) return;
          const userId = p.userId;
          const actorId = p.actorId;
          if (!isDefined(actorId) && !isDefined(userId)) return;
          postSpeakerChangeRequest(
            this.transcriptionId,
            opts.startIndex,
            null,
            null,
            false,
            false,
            shouldMapMatching,
            userId,
            actorId
          )
            .then((t) => {
              this.$emit("updated", t);
              me.$notifySuccess("Successfully updated speaker");
            }).catch((ex) => {
              me.$notifyError(`Unable to update Speaker: ${ex.message}`, ex);
            });
        },
      });
    },
    markClear() {
      if (this.disabled) return;
      const span = this.focusedNodes[0];
      if (!span) return;
      const start = span.dataset.s;
      const end = span.dataset.e;
      postMarkClear(this.transcriptionId, start, end, span.innerText).then((t) => {
        this.$emit("updated", t);
        this.$notifySuccess("Selected text marked as clear");
      }).catch((ex) => {
        this.$notifyError("Unable to mark text as clear", ex);
      });
    },
    unRedactContent() {
      if (this.disabled) return;
      let start = null;
      let end = null;
      let content = null;
      if (!this.highlightedRange) {
        const span = this.focusedNodes[0];
        if (!span) return;
        start = span.dataset.s;
        end = span.dataset.e;
        content = span.innerText;
      } else {
        start = this.highlightedRange.start;
        end = this.highlightedRange.end;
        content = this.highlightedRange.text;
      }
      if (this.editSide === "lhs") {
        postRemoveRedaction(this.transcriptionId, start, end, content).then((t) => {
          this.closeMenu();
          this.$emit("updated", t);
          this.$notifySuccess("Redaction removed");
          this.highlightedRange = null;
        }).catch((ex) => {
          this.closeMenu();
          this.$notifyError("Unable to remove redaction", ex);
        });
      }
      if (this.editSide === "rhs") {
        postRemoveTranslationRedaction(this.transcriptionId, start, end, content).then((t) => {
          this.closeMenu();
          this.$emit("updated", t);
          this.$notifySuccess("Redaction removed");
          this.highlightedRange = null;
        }).catch((ex) => {
          this.closeMenu();
          this.$notifyError("Unable to remove redaction", ex);
        });
      }
    },
    redactContent() {
      if (this.disabled) return;
      let start = null;
      let end = null;
      let content = null;
      if (!this.highlightedRange) {
        const span = this.focusedNodes[0];
        if (!span) return;
        start = span.dataset.s;
        end = span.dataset.e;
        content = span.innerText;
      } else {
        start = this.highlightedRange.start;
        end = this.highlightedRange.end;
        content = this.highlightedRange.text;
      }
      if (this.editSide === "lhs") {
        postRedaction(
          this.transcriptionId,
          start,
          end,
          content
        ).then((t) => {
          this.closeMenu();
          this.$emit("updated", t);
          this.$notifySuccess("Selected text marked as redacted");
          this.highlightedRange = null;
        }).catch((ex) => {
          this.closeMenu();
          this.$notifyError("Unable to redact selected text", ex);
        });
      }
      if (this.editSide === "rhs") {
        postTranslationRedaction(
          this.transcriptionId,
          start,
          end,
          content
        ).then((t) => {
          this.closeMenu();
          this.$emit("updated", t);
          this.$notifySuccess("Selected text marked as redacted");
          this.highlightedRange = null;
        }).catch((ex) => {
          this.closeMenu();
          this.$notifyError("Unable to redact selected text", ex);
        });
      }
    },
    createIncident() {
      if (this.disabled) return;
      this.$refs.incidentForm.display(this.highlightedRange);
      this.highlightedRange = null;
    },

    assignToSpeaker() {
      if (this.disabled) return;
      if (this.highlightedRange == null && this.rightClickTarget != null) {
        let startNode = null;
        let endNode = null;
        // highlights the speaker content based on where the right click was,
        // if a speaker tag was right clicked, it will emulate a click on it
        if (this.rightClickTarget.className.indexOf("span-container") > -1) {
          // span container was right clicked
          startNode = this.rightClickTarget.firstChild;
          endNode = this.rightClickTarget.lastChild;
        } else if (this.rightClickTarget.dataset.spk != null) {
          // speaker node was right clicked, emulate click on speaker
          const s = this.$refs[`spk-${this.rightClickTarget.dataset.spk}`];
          s[0].select();
          return;
        } else if (this.rightClickTarget instanceof HTMLSpanElement) {
          // span was right clicked
          startNode = this.rightClickTarget.parentNode.firstChild;
          endNode = this.rightClickTarget.parentNode.lastChild;
        } else if (this.rightClickTarget instanceof HTMLDivElement) {
          // div was right clicked
          const spanContainer = this.rightClickTarget.nextSibling;
          startNode = spanContainer.firstChild;
          endNode = spanContainer.lastChild;
        } else {
          // something else was right clicked
          return;
        }
        this.highlightedRange = {
          start: startNode.dataset.s,
          end: endNode.dataset.e,
          startTimeOffset: startNode.dataset.so,
          endTimeOffset: startNode.dataset.eo,
          text: this.content.slice(startNode.dataset.s, endNode.dataset.e),
          type: IncidentType.transcript,
          transcriptId: this.transcriptionId,
          uploadId: this.uploadId,
          nodes: {
            start: startNode,
            end: endNode,
          },
        };
        this.highlightOnContentRange(startNode, endNode, "selected");
      }

      if (this.highlightedRange != null) {
        const startNode = this.highlightedRange.nodes.start;
        const endNode = this.highlightedRange.nodes.end;
        this.highlightOnContentRange(startNode, endNode, "selected");
        this.assignSpeakerEventData = Object.assign({}, this.highlightedRange);
        this.$refs.assignToSpeakerForm.showModal = true;
      }
    },
    closeAssignSpeakerForm() {
      this.closeMenu();
    },
    onClipRange(clipRange) {
      if (clipRange) {
        console.log("clipRange in", clipRange.in, "out", clipRange.out);
      }
      this.clipRange = clipRange;
    },
    viewIncident() {
      const spanS = this.focusedNodes[0].dataset.s;
      const spanE = this.focusedNodes[0].dataset.e;
      try {
        // viewIncident only happens on LHS, so we can plug in trSpan-lhs
        const incident = this.$refs[`trSpan-lhs${spanS}-${spanE}`][0]?.incident;
        this.viewClip(incident);
      } catch (ex) {
        console.warn(ex);
      }
    },
    viewClip(incident) {
      if (isDefined(incident) && isDefined(incident.clipId)) {
        this.$router.push({
          name: ethosRouteNames.ClipV2,
          params: {clipId: incident.clipId},
          query: {filterTerm: this.filterTerm || ""},
        });
      }
    },
    editIncident(incident) {
      if (this.disabled) return;
      if (!this.displayIncidents) {
        this.viewClip(incident);
        return;
      }
      this.$refs.incidentForm.display(
        {
          start: incident.startOffset,
          end: incident.endOffset,
          text: this.content.slice(incident.startOffset, incident.endOffset),
          type: IncidentType.transcript,
          transcriptId: this.transcriptionId,
          uploadId: this.uploadId,
        },
        incident
      );
      this.highlightedRange = null;
    },
    approve(data) {
      if (this.disabled) return;
      this.spanContainerKey++;
      postChangeRequestApproval(this.transcriptionId, data.cr.id, true, data.reason).then((t) => {
        this.$emit("updated", t);
        this.$notifySuccess("Change approved");
      }).catch((ex) => {
        this.$notifyError("Unable to approve change", ex);
      });
    },
    reject(data) {
      if (this.disabled) return;
      this.spanContainerKey++;
      postChangeRequestApproval(this.transcriptionId, data.cr.id, false, data.reason).then((t) => {
        this.$emit("updated", t);
        this.$notifySuccess("Change successfully rejected");
      }).catch((ex) => {
        this.$notifyError("Unable to reject the change", ex);
      });
    },
    undoChangeRequest(data) {
      if (this.disabled) return;
      if (data.side === "lhs") {
        postChangeRequests(
          this.transcriptionId,
          [{startIndex: data.s, endIndex: data.e, newContent: data.f, oldContent: data.t}]
        ).then((t) => {
          this.spanContainerKey++;
          this.$emit("updated", t);
          this.$notifySuccess("Undo processed successfully");
        }).catch((ex) => {
          this.$notifyError("Unable to complete undo", ex);
        });
      }
      if (data.side === "rhs") {
        postTranslationChangeRequests(
          this.transcriptionId,
          [{startIndex: data.s, endIndex: data.e, newContent: data.f, oldContent: data.t}]
        ).then((t) => {
          this.spanContainerKey++;
          this.$emit("updated", t);
          this.$notifySuccess("Undo processed successfully");
        }).catch((ex) => {
          this.$notifyError("Unable to complete undo", ex);
        });
      }
    },
    goToNextSpeaker() {
      this.adjacentSpeaker(true);
    },
    goToPrevSpeaker() {
      this.adjacentSpeaker(false);
    },
    adjacentSpeaker(forwards) {
      if (this.disabled) return;
      const editing = this.editingSpeakerAt;
      this.doSaveEdits().then(() => {
        const lastSpeaker = document.querySelector(`div[data-i="${editing}"]`);
        let sibling = forwards ? lastSpeaker.nextSibling : lastSpeaker.previousSibling;
        while (sibling !== null && sibling.className && sibling.className.indexOf("span-container") === -1) {
          sibling = forwards ? sibling.nextSibling : sibling.previousSibling;
        }
        if (sibling != null) {
          try {
            this.focusedSection = parseInt(sibling.dataset["i"], 10);
            this.menuItemEdit(forwards ? "forwards" : "reverse");
          } catch (ex) {
            console.log("Unable to find section index");
          }
        }
      }).catch((ex) => {
        this.doCancelEdits();
        this.$notifyError("Cancelling edits, unable to save", ex);
      });
    },
    editMetadata(e) {
      if (this.disabled) return;
      // with v2 this path should now be only used for the clip form display,
      // the metadataForm has been moved higher level to the ViewUploads component
      // where the transcript tabs live. 11/23/23 jk
      const clip = this.clip;
      if (isDefined(clip)) {
        this.$refs.clipForm.display(0, 0, clip);
      }
    },
    createdIncident(incident) {
      this.$emit("incident", incident);
    },
    updatedIncident(incident) {
      this.$emit("incident", incident);
    },
    showRevertConfirmation() {
      this.closeMenu();
      if (this.editSide === "lhs") {
        this.revertMessage = "This will discard all changes and restore the original TRANSCRIPTION. If there is a translation, it will be deleted. Do you want to proceed?";
        this.showRevertConfirm = true;
      } else if (this.editSide === "rhs") {
        this.revertMessage = "This will discard all changes and restore the original TRANSLATION. The transcription will be unaffected. Do you want to proceed?";
        this.showRevertConfirm = true;
      }
    },
    revertChanges() {
      this.showRevertConfirm = false;
      this.loading = true;
      if (this.editSide === "lhs") {
        postRevertTranscription(this.transcriptionId).then((data) => {
          this.$emit("updated", data);
          this.$notifySuccess("Successfully reverted transcription to base version");
        }).catch((ex) => {
          this.$notifyError("Unable to revert changes");
        }).finally(() => {
          this.loading = false;
        });
      }
      if (this.editSide === "rhs") {
        postRevertTranslation(this.transcriptionId).then((data) => {
          this.$emit("updated", data);
          this.$notifySuccess("Successfully reverted translation to base version");
        }).catch((ex) => {
          this.$notifyError("Unable to revert changes");
        }).finally(() => {
          this.loading = false;
        });
      }
    },
    formatTime(float) {
      let allSeconds = Math.round(float);
      const seconds = allSeconds % 60;
      allSeconds -= seconds;
      const minutes = Math.floor(allSeconds / 60) % 60;
      allSeconds -= minutes * 60;
      const hours = Math.floor(allSeconds / (60 * 60)) % 60;
      return `+ ${`${hours}`.padStart(2, "0")}:${`${minutes}`.padStart(2, "0")}:${`${seconds}`.padStart(2, "0")}`;
    },
    copyEventListener(e) {
      e.preventDefault();
      this.copyHighlightedText();
    },
    async copyHighlightedText(closeMenu = false) {
      const range = this.selection;
      const docFragment = range.cloneContents();
      const text = convertTranscriptDomToText(docFragment);
      try {
        await navigator.clipboard.writeText(text.trim());
        this.$notifySuccess("Copied to clipboard.");
      } catch (error) {
        console.error(error.message);
      }
      if (closeMenu) this.closeMenu();
    },
    addCaseKeyword() {
      // Remove punctuation but preserve international characters
      const trimmedText = this.highlightedRange.text.trim().replace(/\p{P}/gu, "");
      if (trimmedText) {
        this.$refs.addKeyword.addNewKeyword(trimmedText);
      }
      this.closeMenu();
    },
    findTopmostVisibleElement() {
      const scrollContainer = document.querySelector(".upload-content");
      // Get scroll container's viewport bounds
      const containerRect = scrollContainer.getBoundingClientRect();
      const containerTop = containerRect.top;
      // Within the sole element in scrollContainer, find the first element that is visible at the top
      const scrollingObject = this.$refs.parentDiv;
      const children = Array.from(scrollingObject.children);
      for (let i = 0; i < children.length; i++) {
        const element = children[i];
        const elementRect = element.getBoundingClientRect();
        console.log(
          "containerTop", containerRect.top,
          "elementTop", elementRect.top
        );
        // Check if element is visible and intersects with container top
        if (elementRect.bottom > containerTop && elementRect.top <= containerTop + 10) {
          // console.log(
          //   "Got It! ==> containerTop", containerRect.top,
          //   "elementTop", elementRect.top
          // );
          return {
            element: element,
            index: i,
            offsetFromTop: containerTop - elementRect.top,
          };
        }
      }
      return null;
    },
    captureScrollState() {
      const topmostElement = this.findTopmostVisibleElement();
      if (topmostElement) {
        return {
          elementIndex: topmostElement.index,
          offsetFromTop: topmostElement.offsetFromTop,
        };
      }
      return null;
    },
    restoreScrollState(scrollState) {
      if (!scrollState) return;
      this.$nextTick(() => {
        const scrollContainer = document.querySelector(".upload-content");
        const children = Array.from(scrollContainer.children);
        const targetElement = children[scrollState.elementIndex];
        if (targetElement) {
          const elementTop = targetElement.offsetTop;
          const newScrollTop = elementTop - scrollState.offsetFromTop;
          scrollContainer.scrollTop = Math.max(0, newScrollTop);
        }
      });
    },
    restoreScrollState2() {
      this.$nextTick(() => {
        const scrollContainer = document.querySelector(".upload-content");
        scrollContainer.scrollTop = 500;
      });
    },


  },
};
</script>

<style>

.text-selectable {
    user-select: text;
    -webkit-user-select: text;
    -moz-user-select: text;
    -ms-user-select: text;
}

.text-unselectable {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
}

.translation-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 50px;
}

.translation-box {
  padding: 30px;
  background-color: #202828;
  border-radius: 8px;
  border: 1px solid #666666;
}

</style>

